import React, { useEffect, useState } from "react";
import { FiSearch, FiHeart, FiUser, FiShoppingCart } from "react-icons/fi";
import { FaBars, FaTimes } from "react-icons/fa";
import Logo1 from "../../assets/logo1.png";
import LoginModal from "../loginModal";
import LoginModalContext from "../../contexts/LoginModalContext";
import { fetchMasterCategories } from "../../../node_modules/shops-query/src/modules/masterCategories/index";
import { fetchSecondaryCategories } from "../../../node_modules/shops-query/src/modules/SecondaryCategories/index";
import { shopid, imagePrefix, getUserId } from "../../utils/url";
import { useNavigate, useLocation } from "react-router-dom";
import UserMenuModal from "../userMenuModal";
import Cookies from "js-cookie";
import ConfirmationDialog from "../confirmationModal";
import { useToast } from "../toast";
import Loader from "../loading/index";
import { getProductsBySpecification } from "../../../node_modules/shops-query/src/modules/productBySpecification/index.js";
import { fetchCart } from "../../../node_modules/shops-query/src/modules/cart/index";
import { getGuestCart } from "../../utils/handleGuestCart";

import { useContext } from "react";

const Header = ({ loginModalOpen, onCloseLoginModal }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToSection = (id) => {
    const scrollWithOffset = () => {
      const element = document.getElementById(id);
      if (element) {
        const headerOffset = 100; // Adjustment for sticky header
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    };

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(scrollWithOffset, 500);
    } else {
      scrollWithOffset();
    }
  };
  const { openLogin } = useContext(LoginModalContext);
  const [showLoggedInModal, setShowLoggedInModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [subcategoriesMap, setSubcategoriesMap] = useState({});
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [menuCloseTime, setMenuCloseTime] = useState(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const { showToast, ToastContainer } = useToast();
  const [cartCount, setCartCount] = useState(0);
  const userId = getUserId();

  // Load cart count initially and on userId change
  useEffect(() => {
    const loadCount = async () => {
      try {
        if (userId) {
          const data = await fetchCart(shopid, userId);
          const count = Array.isArray(data)
            ? data.reduce((sum, item) => sum + (item.quantity || 1), 0)
            : 0;
          setCartCount(count);
        } else {
          const guest = getGuestCart();
          const count = Array.isArray(guest)
            ? guest.reduce((sum, item) => sum + (item.quantity || 1), 0)
            : 0;
          setCartCount(count);
        }
      } catch (e) {
        setCartCount(0);
      }
    };
    loadCount();
  }, [userId]);

  // Listen for cart updates (guest or logged-in)
  useEffect(() => {
    const loadCount = async () => {
      try {
        if (userId) {
          const data = await fetchCart(shopid, userId);
          const count = Array.isArray(data)
            ? data.reduce((sum, item) => sum + (item.quantity || 1), 0)
            : 0;
          setCartCount(count);
        } else {
          const guest = getGuestCart();
          const count = Array.isArray(guest)
            ? guest.reduce((sum, item) => sum + (item.quantity || 1), 0)
            : 0;
          setCartCount(count);
        }
      } catch (e) {
        setCartCount(0);
      }
    };

    window.addEventListener("cartUpdated", loadCount);
    return () => window.removeEventListener("cartUpdated", loadCount);
  }, [userId]);
  // Header search visibility state
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // State for inline search in header
  const [headerSearchData, setHeaderSearchData] = useState(null);
  const [headerSearchLoading, setHeaderSearchLoading] = useState(false);
  const [headerSearchError, setHeaderSearchError] = useState(null);

  // Fetch products for header search when debounced search changes
  useEffect(() => {
    const fetchHeaderSearchProducts = async () => {
      if (!debouncedSearch) {
        setHeaderSearchData(null);
        setHeaderSearchLoading(false);
        return;
      }

      try {
        setHeaderSearchLoading(true);
        setHeaderSearchError(null);
        const results = await getProductsBySpecification({
          shopId: shopid,
          specifications: [],
          searchKey: debouncedSearch,
        });
        setHeaderSearchData({ productBySpecification: results });
      } catch (error) {
        console.error('Error fetching header search results:', error);
        setHeaderSearchError(error);
      } finally {
        setHeaderSearchLoading(false);
      }
    };

    fetchHeaderSearchProducts();
  }, [debouncedSearch]);

  const allCategories = [...categories];

  const handleMobileMenu = () => setMobileMenu((prev) => !prev);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const data = await fetchMasterCategories(shopid);
        setCategories(data?.length ? data : []);
      } catch (err) {
        console.error("Fetch error:", err);
        setError(err.message || "Failed to load categories");
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    if (shopid) fetchCategories();
    else {
      console.error("shopid is missing");
      setCategories([]);
    }
  }, []);

  const fetchAllSubCategories = async () => {
    try {
      setLoadingSubcategories(true);
      const newMap = {};
      for (const cat of allCategories) {
        try {
          const data = await fetchSecondaryCategories(shopid, cat.id);
          const apiSubs = data?.map((s) => s.category) || [];
          newMap[cat.id] = apiSubs;
        } catch (err) {
          console.error(`Error fetching subcategories for ${cat.id}:`, err);
          newMap[cat.id] = [];
        }
      }
      setSubcategoriesMap(newMap);
    } catch (err) {
      console.error("Error in fetchAllSubcategories:", err);
    } finally {
      setLoadingSubcategories(false);
    }
  };

  const handleMenuHover = () => {
    setMegaMenuOpen(true);
    // Only fetch if we haven't loaded subcategories yet
    if (Object.keys(subcategoriesMap).length === 0) {
      fetchAllSubCategories();
    }
  };

  const handleMouseLeave = () => {
    if (menuCloseTime) clearTimeout(menuCloseTime);
    const timeout = setTimeout(() => setMegaMenuOpen(false), 200);
    setMenuCloseTime(timeout);
  };

  const cancelMenuClose = () => {
    if (menuCloseTime) {
      clearTimeout(menuCloseTime);
      setMenuCloseTime(null);
    }
  };

  const handleLogout = () => {
    Cookies.remove("ualum");
    setShowConfirmationModal(false);
    showToast({
      message: "Logged out successfully",
      type: "success",
    });
    setShowLoggedInModal(false);
    navigate("/");
  };

  return (
    <div className="sticky top-0 z-50 w-full bg-white text-black font-poppins">
      <div className="flex items-center justify-between px-4 md:px-8 h-[70px] md:h-[80px] relative shadow-md">
        {/* Left: Mobile menu icon */}
        <div className="md:hidden mt-2">
          <button onClick={handleMobileMenu}>
            {mobileMenu ? <FaTimes size={24} /> : <FaBars size={24} />}
          </button>
        </div>

        {/* Center section (Logo + Categories) */}
        <div className="flex items-center justify-center">
          {/* Logo */}
          <div>
            <img
              src={Logo1}
              alt="Logo"
              onClick={() => {
                navigate("/");
              }}
              className="h-8  mr-20 md:h-10 md:ml-8 cursor-pointer"
            />
          </div>
        </div>
        {/* Categories — Desktop only (centered) */}
        <div className="hidden md:flex relative justify-center flex-1 items-center space-x-8 font-medium">
          <button
             className="hover:text-[#D54420] font-normal"
            onClick={() => navigate("/")}
          >
            Home
          </button>
          <button
             className="hover:text-[#D54420] font-normal"
             onClick={() => scrollToSection("new-arrivals")}
           >
             New Arrivals
           </button>
          {allCategories.map((item) => (
            <div
              key={item.id}
              className="relative group"
              onMouseEnter={() => {
                setHoveredCategory(item);
                handleMenuHover();
              }}
              onMouseLeave={handleMouseLeave}
            >
              <button className="hover:text-[#D54420] font-normal">
                {item.category}
              </button>
            </div>
          ))}
          <button
            className="hover:text-[#D54420] font-normal"
            onClick={() => scrollToSection("about-us")}
          >
            About Us
          </button>
        </div>

        {/* Right: Inline Search (desktop) + Icons */}
        {/* Icons and Search Section */}
        <div className="flex items-center space-x-4 md:space-x-8 text-lg sm:mr-[20px] md:mr-[50px]">
          {/* Search Section */}
          <div className={`${isSearchVisible ? "absolute inset-0 bg-white z-[60] px-4 flex items-center md:relative md:inset-auto md:bg-transparent md:px-0" : "relative"} flex items-center`}>
            {!isSearchVisible ? (
              <FiSearch
                size={24}
                className="cursor-pointer hover:text-[#D54420] transition-colors"
                onClick={() => setIsSearchVisible(true)}
              />
            ) : (
              <div className="flex items-center w-full animate-in fade-in slide-in-from-top-1 duration-300">
                <div className="relative flex items-center border-b border-black pb-1 w-full md:w-[250px] lg:w-[300px]">
                  <FiSearch
                    size={20}
                    className="text-black mr-2 bg-transparent shrink-0"
                  />
                  <input
                    autoFocus
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Escape") {
                        if (searchQuery) {
                          setSearchQuery("");
                          setDebouncedSearch("");
                        } else {
                          setIsSearchVisible(false);
                        }
                      }
                    }}
                    placeholder="Search products..."
                    className="w-full text-sm sm:text-base text-black bg-transparent focus:outline-none placeholder:text-gray-500"
                  />
                  {searchQuery ? (
                    <FaTimes
                      size={18}
                      className="ml-2 cursor-pointer text-gray-400 hover:text-black shrink-0"
                      onClick={() => {
                        setSearchQuery("");
                        setDebouncedSearch("");
                      }}
                    />
                  ) : (
                    <FaTimes
                      size={18}
                      className="ml-2 cursor-pointer text-gray-400 hover:text-black shrink-0"
                      onClick={() => setIsSearchVisible(false)}
                    />
                  )}
                </div>

                {/* Results dropdown anchored to input */}
                {debouncedSearch && (
                  <div className="absolute top-full left-0 right-0 md:right-auto mt-0 md:mt-3 w-full md:w-[300px] bg-white border-t md:border border-gray-200 md:rounded-lg shadow-xl max-h-[80vh] md:max-h-[60vh] overflow-auto z-50">
                    {headerSearchLoading ? (
                      <div className="p-4 text-sm text-gray-500 text-center">
                        Searching...
                      </div>
                    ) : headerSearchError && !headerSearchData ? (
                      <div className="p-4 text-sm text-red-500">Error fetching results</div>
                    ) : headerSearchData &&
                      Array.isArray(headerSearchData.productBySpecification) &&
                      headerSearchData.productBySpecification.length > 0 ? (
                      <ul className="divide-y divide-gray-100">
                        {headerSearchData.productBySpecification.slice(0, 8).map((p) => (
                          <li
                            key={p.id}
                            className="p-3 hover:bg-gray-50 cursor-pointer flex items-center gap-3 transition-colors"
                            onClick={() => {
                              setSearchQuery("");
                              setDebouncedSearch("");
                              setIsSearchVisible(false);
                              navigate(`/product/${p.id}`);
                            }}
                          >
                            <img
                              src={imagePrefix + p.featureImage}
                              alt={p.name}
                              className="w-12 h-12 object-cover rounded shadow-sm"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-900 truncate">
                                {p.name}
                              </div>
                              {typeof p.prize === "number" && (
                                <div className="text-xs font-medium text-[#D54420]">
                                  ₹{Math.round(p.prize)}
                                </div>
                              )}
                            </div>
                          </li>
                        ))}
                        <li className="p-2 text-[11px] text-center text-gray-400 bg-gray-50">
                          Found {headerSearchData.productBySpecification.length} products
                        </li>
                      </ul>
                    ) : (
                      <div className="p-4 text-sm text-gray-500 text-center">No results found</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Always Visible Action Icons */}
          <div className="flex items-center space-x-4 md:space-x-6">
            <FiHeart
              size={24}
              className="cursor-pointer hover:text-[#D54420] transition-colors"
              onClick={() =>{
                if (Cookies.get("ualum")) {
                   navigate("/wishlist")
                  } else {
                    openLogin();
                  }
              }}
            />
            <FiUser
              size={24}
              className="cursor-pointer hover:text-[#D54420] transition-colors"
              onClick={() => {
                if (Cookies.get("ualum")) {
                  setShowLoggedInModal(!showLoggedInModal);
                } else {
                  openLogin();
                }
              }}
            />
            <div className="relative group">
              <FiShoppingCart
                size={24}
                className="cursor-pointer group-hover:text-[#D54420] transition-colors"
                onClick={() => navigate("/cart")}
              />
              {cartCount > 0 && (
                <span
                  className="absolute -top-2 -right-3 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center text-[10px] font-bold bg-white border border-[#FF4C24] text-[#FF4C24]"
                >
                  {cartCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Inline Search Bar removed from below; now anchored to icon */}

      {/* Desktop Mega Menu */}
      {megaMenuOpen && (
        <div
          className="hidden md:flex absolute left-1/2 top-full bg-white shadow-lg border border-gray-300 p-8 w-[90vw] max-w-[1372px] z-50 justify-center gap-12 -translate-x-1/2 flex-wrap -mt-6"
          onMouseEnter={cancelMenuClose}
          onMouseLeave={handleMouseLeave}
        >
          {loadingSubcategories ? (
            // Loading state for desktop mega menu
            <div className="w-full flex justify-center items-center py-8">
              <div className="flex flex-col items-center space-y-3">
                <Loader color="#10B981" size="md" />
              </div>
            </div>
          ) : (
            // Categories content when loaded
            allCategories.map((cat) => (
              <div
                key={cat.id}
                className="border-r border-gray-200 pr-6 last:border-r-0 text-left"
              >
                <p className="font-semibold text-black mb-3">{cat.category}</p>
                <ul className="space-y-2 text-sm text-gray-700">
                  {subcategoriesMap[cat.id]?.length ? (
                    subcategoriesMap[cat.id].map((sub, i) => (
                      <li
                        key={i}
                        className="hover:text-[#D54420] cursor-pointer"
                        onClick={() => {
                          navigate(
                            `/all-products?mainCategory=${encodeURIComponent(
                              cat.category
                            )}&category=${encodeURIComponent(sub)}`
                          );
                          setMegaMenuOpen(false);
                        }}
                      >
                        {sub}
                      </li>
                    ))
                  ) : (
                    <li className="text-gray-400">No subcategories</li>
                  )}
                </ul>
              </div>
            ))
          )}
        </div>
      )}

      {/* Mobile Dropdown Menu */}
      <div
        className={`md:hidden fixed top-[70px] left-0 w-full bg-white text-black shadow-lg z-50 transition-all duration-300 overflow-y-auto ${mobileMenu ? "max-h-[80vh] opacity-100" : "max-h-0 opacity-0"
          }`}
      >
        <div className="flex flex-col divide-y divide-gray-200">
          <button
            onClick={() => {
              navigate("/");
              setMobileMenu(false);
            }}
            className="w-full flex justify-between items-center px-5 py-4 font-medium text-left"
          >
            <span className="text-[18px]">Home</span>
          </button>
          <button
            onClick={() => {
              scrollToSection("new-arrivals");
              setMobileMenu(false);
            }}
            className="w-full flex justify-between items-center px-5 py-4 font-medium text-left"
          >
            <span className="text-[18px]">New Arrivals</span>
          </button>
          {allCategories.map((item) => (
            <div key={item.id}>
              {/* Main Category */}
              <button
                onClick={async () => {
                  if (hoveredCategory?.id === item.id) {
                    setHoveredCategory(null);
                    setMegaMenuOpen(false);
                  } else {
                    setHoveredCategory(item);
                    setMegaMenuOpen(true);
                    if (!subcategoriesMap[item.id]) {
                      try {
                        const data = await fetchSecondaryCategories(
                          shopid,
                          item.id
                        );
                        const apiSubs = data?.map((s) => s.category) || [];
                        setSubcategoriesMap((prev) => ({
                          ...prev,
                          [item.id]: apiSubs,
                        }));
                      } catch (err) {
                        console.error("Error fetching subcategories:", err);
                      }
                    }
                  }
                }}
                className="w-full flex justify-between items-center px-5 py-4 font-medium text-left"
              >
                <span className="text-[18px]">{item.category}</span>
                <span className="text-gray-600 text-sm">
                  {hoveredCategory?.id === item.id ? "−" : "+"}
                </span>
              </button>

              {/* Subcategories */}
              {megaMenuOpen && hoveredCategory?.id === item.id && (
                <div className="bg-gray-50 px-6 py-4">
                  <ul className="space-y-2 text-sm text-gray-700 text-[16px]">
                    {subcategoriesMap[item.id]?.length ? (
                      subcategoriesMap[item.id].map((sub, i) => (
                        <li
                          key={i}
                          className="pl-2 cursor-pointer hover:text-black"
                          onClick={() => {
                            navigate(
                              `/all-products?mainCategory=${encodeURIComponent(
                                item.category
                              )}&category=${encodeURIComponent(sub)}`
                            );
                            setMobileMenu(false);
                            setMegaMenuOpen(false);
                          }}
                        >
                          {sub}
                        </li>
                      ))
                    ) : (
                      <li className="pl-2 text-gray-400">No subcategories</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          ))}
          <button
            onClick={() => {
              scrollToSection("about-us");
              setMobileMenu(false);
            }}
            className="w-full flex justify-between items-center px-5 py-4 font-medium text-left"
          >
            <span className="text-[18px]">About Us</span>
          </button>
        </div>
      </div>

      {/* Login Modal */}
      <LoginModal isOpen={loginModalOpen} onClose={onCloseLoginModal} />

      {showLoggedInModal && (
        <UserMenuModal
          onClose={() => setShowLoggedInModal(false)}
          onProfile={() => {
            navigate("/profile");
            setShowLoggedInModal(false);
          }}
          onWishlist={() => {
            navigate("/wishlist");
            setShowLoggedInModal(false);
          }}
          onOrders={() => {
            navigate("/order-history");
            setShowLoggedInModal(false);
          }}
          onLogout={() => {
            setShowConfirmationModal(true);
          }}
        />
      )}
      {showConfirmationModal && (
        <ConfirmationDialog
          isOpen={showConfirmationModal}
          title="Are You Sure You Want To Logout?"
          message="You'll need to log in again to access your account."
          confirmLabel="LOGOUT"
          cancelLabel="CANCEL"
          onConfirm={handleLogout}
          onCancel={() => setShowConfirmationModal(false)}
        />
      )}
    </div>
  );
};
export default Header;